import * as Sentry from '@sentry/react';
import { BrowserOptions as SentryBrowserOptions } from '@sentry/browser';
import { HttpClient } from '@sentry/integrations';
import { History } from 'history';
import { getTenancy } from '../hooks/useTenancy';
import { getDomainConfig } from './domainConfig';
import { isNetworkError, isPageHidden } from './fi-api/networkRetry';

/**
 * Sentry.io configuration
 */
function getSentryConfig(history?: History): SentryBrowserOptions {
  const baseConfig: SentryBrowserOptions = {
    dsn: 'https://941c0176eac6d78488430189ded5a5c2@o4506027582619648.ingest.sentry.io/4506061077676032',
    environment: process.env.REACT_APP_ENVIRONMENT,
    // ties each event to the deploy that shipped it, so Sentry can flag an issue that regresses in a release
    release: process.env.REACT_APP_VERSION !== 'unknown' ? process.env.REACT_APP_VERSION : undefined,
    // Errors thrown inside OneTrust's own cookie-banner SDK (cdn.cookielaw.org), which we can't fix from here.
    denyUrls: [/cdn\.cookielaw\.org/],
    // Disable Sentry for test tenancy, and for browsers driven by automation (the Playwright suites, including the
    // hourly BrowserStack run against production), whose errors come from the test harness and its browsers rather
    // than from shoppers
    enabled: getTenancy() !== 'test' && !navigator.webdriver,
    // Errors that aren't ours and that shoppers don't notice.
    ignoreErrors: [
      // Thrown by scripts the Facebook and Instagram in-app browsers inject into every page, not by our code, which
      // never touches a native bridge.
      // iOS autofill script.
      /_AutofillCallbackHandler/,
      // iOS bridge script, run where the native bridge it expects is absent.
      /window\.webkit\.messageHandlers/,
      // iOS frame script, run before the app defines window._META.
      /window\._META\.IABInnerFrame/,
      // Android bridge (iabjs:// scripts), called after the app destroyed it.
      /Java object is gone/,
      // Segment's Bing Ads integration, when a content blocker blocks or stubs out Bing's UET script.
      /new window\.UET/,
      // The browser's notice that a ResizeObserver callback resized something it observes; the next frame delivers the
      // pending notifications, so nothing is lost.
      /ResizeObserver loop/,
    ],
    // A request cut off because the shopper left the page or app (iOS WebKit aborts in-flight requests when a tab is
    // hidden) has nobody waiting on it. Requests that drop while the page is visible are retried first, then reported.
    // A reported drop is tagged with the retries it used; the ones a retry rescued are counted in analytics instead.
    beforeSend: (event, hint) => {
      if (!isNetworkError(hint.originalException)) {
        return event;
      }
      if (isPageHidden()) {
        return null;
      }
      const networkRetryAttempt = hint.originalException.config?.networkRetryAttempt ?? 0;
      return { ...event, tags: { ...event.tags, networkRetryAttempt } };
    },
  };

  // RUM = Real User Monitoring
  if (process.env.REACT_APP_RUM_ENABLE === 'true') {
    return {
      ...baseConfig,
      integrations: [
        // Creates events for failed client side HTTP requests
        new HttpClient(),
        // Performance Monitoring. Without a react-router history, page loads and navigations are named by URL.
        new Sentry.BrowserTracing(
          history ? { routingInstrumentation: Sentry.reactRouterV5Instrumentation(history) } : {},
        ),
        // Session Replay
        new Sentry.Replay({
          blockAllMedia: false, // default is true
          maskAllText: true,
        }),
      ],
      // Session Replay
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 0.1, // TODO: bump this up to 100% after we fix the high frequency errors
      // Performance Monitoring
      tracePropagationTargets: ['localhost', /^https:\/\/api.tryfi.com/],
      tracesSampleRate: 0.01,
    };
  }

  return baseConfig;
}

/**
 * Starts Sentry in the browser for one of the two apps, tagging every event with which one sent it. Both bundles
 * share Sentry's global hub, so if the other app already started it on this page, its client is kept as it is.
 */
export function initSentry(app: 'legacy' | 'main', history?: History) {
  if (Sentry.getCurrentHub().getClient()) {
    return;
  }
  Sentry.init(getSentryConfig(history));
  Sentry.setTag('app', app);
  Sentry.setTag('tenancy', getTenancy());
  Sentry.setTag('domain', getDomainConfig().baseDomain);
}
